import React, { useState, useEffect, useCallback } from 'react';

// PrimeReact Imports
import { InputTextarea } from 'primereact/inputtextarea';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { SelectButton } from 'primereact/selectbutton';
import { Panel } from 'primereact/panel';
import { Badge } from 'primereact/badge';
import { Divider } from 'primereact/divider';

// Lista ampliada de Stop Words (Inglés y Español)
const STOP_WORDS = new Set([
    'a', 'about', 'above', 'after', 'again', 'against', 'all', 'am', 'an', 'and', 'any', 'are', 'aren\'t', 'as', 'at', 
    'be', 'because', 'been', 'before', 'being', 'below', 'between', 'both', 'but', 'by', 
    'can', 'cannot', 'could', 'couldn\'t', 
    'did', 'didn\'t', 'do', 'does', 'doesn\'t', 'doing', 'don\'t', 'down', 'during', 
    'each', 
    'few', 'for', 'from', 'further', 
    'had', 'hadn\'t', 'has', 'hasn\'t', 'have', 'haven\'t', 'having', 'he', 'he\'d', 'he\'ll', 'he\'s', 'her', 'here', 'here\'s', 'hers', 'herself', 'him', 'himself', 'his', 'how', 'how\'s', 
    'i', 'i\'d', 'i\'ll', 'i\'m', 'i\'ve', 'if', 'in', 'into', 'is', 'isn\'t', 'it', 'it\'s', 'its', 'itself', 
    'let\'s', 
    'me', 'more', 'most', 'mustn\'t', 'my', 'myself', 
    'no', 'nor', 'not', 
    'of', 'off', 'on', 'once', 'only', 'or', 'other', 'ought', 'our', 'ours', 'ourselves', 'out', 'over', 'own', 
    'same', 'shan\'t', 'she', 'she\'d', 'she\'ll', 'she\'s', 'should', 'shouldn\'t', 'so', 'some', 'such', 
    'than', 'that', 'that\'s', 'the', 'their', 'theirs', 'them', 'themselves', 'then', 'there', 'there\'s', 'these', 'they', 'they\'d', 'they\'ll', 'they\'re', 'they\'ve', 'this', 'those', 'through', 'to', 'too', 
    'under', 'until', 'up', 
    'very', 
    'was', 'wasn\'t', 'we', 'we\'d', 'we\'ll', 'we\'re', 'we\'ve', 'were', 'weren\'t', 'what', 'what\'s', 'when', 'when\'s', 'where', 'where\'s', 'which', 'while', 'who', 'who\'s', 'whom', 'why', 'why\'s', 'with', 'won\'t', 'would', 'wouldn\'t', 
    'you', 'you\'d', 'you\'ll', 'you\'re', 'you\'ve', 'your', 'yours', 'yourself', 'yourselves',
    // Español
    'el', 'la', 'los', 'las', 'un', 'una', 'unos', 'unas', 'y', 'o', 'pero', 'si', 'de', 'del', 'al', 'se', 'su', 'sus', 'es', 'en', 'por', 'para', 'con', 'lo', 'no', 'si', 'dos', 'mas', 'más', 'mi', 'mis', 'ti', 'tu', 'tus', 'te', 'yo', 'me', 'nos', 'esa', 'ese', 'eso', 'esta', 'este', 'esto', 'que', 'qué', 'cual', 'cuál', 'quien', 'quién', 'como', 'cómo'
]);

const WordCounterPrime = () => {
    const [text, setText] = useState('');
    const [loadingMetrics, setLoadingMetrics] = useState(false);
    
    const [metrics, setMetrics] = useState({
        words: 0,
        characters: 0,
        paragraphs: 0,
        sentences: 0,
        readingTime: '0 sec',
        speakingTime: '0 sec',
        density: []
    });

    const [densityLevel, setDensityLevel] = useState(1);

    const densityOptions = [
        { label: 'Density x1', value: 1 },
        { label: 'Density x2', value: 2 },
        { label: 'Density x3', value: 3 }
    ];

    const calculateMetrics = useCallback((inputText, level) => {
        if (!inputText) {
            return { 
                words: 0, characters: 0, paragraphs: 0, sentences: 0, 
                readingTime: '0 sec', speakingTime: '0 sec', density: [] 
            };
        }

        // --- Contadores Generales ---
        const charCount = inputText.length;
        const unifiedText = inputText.replace(/\n/g, ' '); 
        const wordsArray = unifiedText.trim().split(/\s+/).filter(w => w.length > 0);
        const wordCount = wordsArray.length;
        const paragraphCount = inputText.split(/\n/).filter(p => p.trim().length > 0).length;
        const sentenceCount = inputText.split(/[.!?]+/).filter(s => s.trim().length > 0).length;

        // Tiempos
        const timeCalc = (wpm) => {
            if (wordCount === 0) return '0 sec';
            const minutes = wordCount / wpm;
            const seconds = Math.floor(minutes * 60);
            if (minutes < 1) return `${seconds} sec`;
            return `${Math.floor(minutes)} min ${seconds % 60} sec`;
        };

        // --- Lógica de Densidad (Keyword Density) ---
        
        // 1. Limpieza: Minúsculas y quitar puntuación pegada
        const cleanWords = unifiedText
            .toLowerCase()
            .replace(/[.,/#!$%^&*;:{}=\-_`~()?"¡¿]/g, "") 
            .split(/\s+/)
            .filter(w => w.length > 0);

        // 2. FILTRADO: Quitamos las Stop Words ANTES de generar los n-grams
        const filteredWords = cleanWords.filter(w => !STOP_WORDS.has(w));

        const frequencyMap = {};
        const totalNgrams = filteredWords.length - level + 1;

        if (totalNgrams > 0) {
            for (let i = 0; i < totalNgrams; i++) {
                // Generamos la frase (keyword) basada solo en palabras relevantes
                const phrase = filteredWords.slice(i, i + level).join(' ');
                
                if (phrase.trim()) {
                    frequencyMap[phrase] = (frequencyMap[phrase] || 0) + 1;
                }
            }
        }

        const densityArray = Object.entries(frequencyMap)
            .map(([keyword, count]) => ({
                keyword,
                count,
                // Calculamos porcentaje sobre el total de palabras (incluyendo stop words)
                // para mantener la proporción real del texto, o puedes usar filteredWords.length
                // si quieres densidad relativa solo al contenido. WordCounter usa total words.
                percentage: ((count / wordCount) * 100).toFixed(1)
            }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 50);

        return {
            words: wordCount,
            characters: charCount,
            paragraphs: paragraphCount,
            sentences: sentenceCount || (wordCount > 0 ? 1 : 0),
            readingTime: timeCalc(275),
            speakingTime: timeCalc(180),
            density: densityArray
        };
    }, []);

    useEffect(() => {
        setLoadingMetrics(true);
        const timer = setTimeout(() => {
            const results = calculateMetrics(text, densityLevel);
            setMetrics(results);
            setLoadingMetrics(false);
        }, 200);

        return () => clearTimeout(timer);
    }, [text, densityLevel, calculateMetrics]);

    const percentageBodyTemplate = (rowData) => {
        return <Badge value={`${rowData.percentage}%`} severity="info" />;
    };

    return (
        <div className="seo-tool-container">
            <div className="seo-header">
                <h2>SEO Editor & Counter</h2>
            </div>

            <div className="seo-layout">
                {/* EDITOR */}
                <div className="seo-editor-column">
                    <Card className="h-full-card">
                        <InputTextarea
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            placeholder="Type or paste your text here..."
                            className="w-full h-full seo-textarea"
                            autoResize={false} 
                        />
                    </Card>
                </div>

                {/* ESTADÍSTICAS */}
                <div className="seo-stats-column">
                    
                    <Panel header="Details" toggleable={false} className="mb-3">
                        <div className="stats-grid">
                            <div className="stat-item">
                                <span className="stat-value">{metrics.words}</span>
                                <span className="stat-label">Words</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-value">{metrics.characters}</span>
                                <span className="stat-label">Chars</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-value">{metrics.sentences}</span>
                                <span className="stat-label">Sentences</span>
                            </div>
                             <div className="stat-item">
                                <span className="stat-value">{metrics.paragraphs}</span>
                                <span className="stat-label">Paragraphs</span>
                            </div>
                             <div className="stat-item">
                                <span className="stat-value-sm">{metrics.readingTime}</span>
                                <span className="stat-label">Reading Time</span>
                            </div>
                             <div className="stat-item">
                                <span className="stat-value-sm">{metrics.speakingTime}</span>
                                <span className="stat-label">Speaking Time</span>
                            </div>
                        </div>
                    </Panel>

                    <Panel header="Keyword Density" className="density-panel">
                        <div className="controls-container">
                            <SelectButton 
                                value={densityLevel} 
                                options={densityOptions} 
                                onChange={(e) => e.value && setDensityLevel(e.value)} 
                                unselectable={false}
                                className="w-full"
                            />
                        </div>
                        <Divider />
                        <DataTable 
                            value={metrics.density} 
                            loading={loadingMetrics}
                            scrollable 
                            scrollHeight="350px" 
                            size="small"
                            stripedRows
                            emptyMessage="No keywords found"
                            className="density-table"
                        >
                            <Column field="keyword" header="Keyword" style={{ width: '50%' }}></Column>
                            <Column field="count" header="Count" sortable style={{ width: '25%' }}></Column>
                            <Column field="percentage" header="%" body={percentageBodyTemplate} sortable style={{ width: '25%' }}></Column>
                        </DataTable>
                    </Panel>
                </div>
            </div>

            <style jsx="true">{`
                .seo-tool-container {
                    padding: 2rem;
                    background-color: #f8f9fa;
                    min-height: 100vh;
                    font-family: var(--font-family);
                }
                .seo-header {
                    margin-bottom: 1.5rem;
                    border-bottom: 1px solid #dee2e6;
                    padding-bottom: 1rem;
                }
                .seo-header h2 { margin: 0; color: #343a40; }
                
                .seo-layout {
                    display: flex;
                    flex-direction: column;
                    gap: 1.5rem;
                    height: calc(100vh - 150px);
                }

                @media (min-width: 992px) {
                    .seo-layout { flex-direction: row; }
                    .seo-editor-column { flex: 0 0 70%; max-width: 70%; }
                    .seo-stats-column { flex: 0 0 30%; max-width: 30%; }
                }

                .h-full-card { height: 100%; display: flex; flex-direction: column; }
                .h-full-card .p-card-body, .h-full-card .p-card-content { 
                    flex: 1; 
                    padding: 0; 
                    height: 100%;
                }

                .seo-textarea {
                    border: none !important;
                    box-shadow: none !important;
                    padding: 1.5rem !important;
                    font-size: 1.1rem;
                    line-height: 1.6;
                    color: #495057;
                    height: 100% !important;
                    background-color: transparent;
                }
                .seo-textarea:focus { outline: none !important; }

                /* Grid para los detalles */
                .stats-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 10px;
                    text-align: center;
                }
                .stat-item {
                    background-color: #f1f3f5;
                    padding: 8px;
                    border-radius: 6px;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                }
                .stat-value { font-size: 1.4rem; font-weight: 700; color: #495057; }
                .stat-value-sm { font-size: 1.1rem; font-weight: 700; color: #495057; }
                .stat-label { font-size: 0.75rem; color: #868e96; text-transform: uppercase; margin-top: 4px; }

                .controls-container { display: flex; justify-content: center; margin-bottom: 0.5rem; }
                .controls-container .p-selectbutton .p-button { font-size: 0.85rem; padding: 0.5rem 1rem; }
                .density-panel .p-panel-content { padding: 0.5rem; }
                .mb-3 { margin-bottom: 1rem; }
            `}</style>
        </div>
    );
};

export default WordCounterPrime;